import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { roomsService, propertiesService } from '../services/api';
import PriceBreakdownButton from './PriceBreakdownButton';
import { useIsMobile } from '../hooks';
import { Building2 as Building2Icon, Home as HomeIcon, Lock as LockIcon, Pencil as PencilIcon, Plus as PlusIcon, RefreshCw as RefreshCwIcon, Trash2 as Trash2Icon } from 'lucide-react';

function PropertyRoomTypeManager({ user, onDataUpdate }) {
  const isMobile = useIsMobile();
  const [activeTab, setActiveTab] = useState('properties');
  const [properties, setProperties] = useState([]);
  const [roomTypes, setRoomTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Property form state
  const [showPropertyModal, setShowPropertyModal] = useState(false);
  const [editingProperty, setEditingProperty] = useState(null);
  const [propertyForm, setPropertyForm] = useState({
    name: '',
    address: ''
  });

  // Room Type form state
  const [showRoomTypeModal, setShowRoomTypeModal] = useState(false);
  const [editingRoomType, setEditingRoomType] = useState(null);
  const [roomTypeForm, setRoomTypeForm] = useState({
    name: '',
    description: '',
    isShared: false,
    defaultRate: ''
  });

  // Check if user has admin privileges
  const isAdmin = user?.role === 'SUPERADMIN' || user?.role === 'PROPERTYADMIN';

  useEffect(() => {
    if (isAdmin) {
      loadData();
    }
  }, [isAdmin]);

  const loadData = async () => {
    try {
      setLoading(true);
      console.log('Loading properties and room types...');
      
      const [propertiesResponse, roomTypesResponse] = await Promise.all([
        propertiesService.getAll({ limit: 100 }),
        roomsService.getRoomTypes()
      ]);

      console.log('Properties response:', propertiesResponse);
      console.log('Room types response:', roomTypesResponse);

      // Extract properties with better error handling
      const properties = propertiesResponse?.data?.properties || 
                        propertiesResponse?.properties || 
                        [];
      
      // Extract room types with better error handling
      const roomTypes = roomTypesResponse?.data?.roomTypes || 
                       roomTypesResponse?.roomTypes || 
                       [];

      console.log('Extracted properties:', properties);
      console.log('Extracted room types:', roomTypes);

      setProperties(properties);
      setRoomTypes(roomTypes);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load data: ' + (error.message || 'Unknown error'));
      // Set empty arrays as fallback
      setProperties([]);
      setRoomTypes([]);
    } finally {
      setLoading(false);
    }
  };

  // Property Management Functions
  const openPropertyModal = (property = null) => {
    setEditingProperty(property);
    setPropertyForm({
      name: property?.name || '',
      address: property?.address || ''
    });
    setShowPropertyModal(true);
  };

  const handlePropertySubmit = async (e) => {
    e.preventDefault();
    if (!propertyForm.name.trim() || !propertyForm.address.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      if (editingProperty) {
        await propertiesService.update(editingProperty.id, propertyForm);
        toast.success('Property updated successfully');
      } else {
        await propertiesService.create(propertyForm);
        toast.success('Property created successfully');
      }
      
      setShowPropertyModal(false);
      setEditingProperty(null);
      setPropertyForm({ name: '', address: '' });
      loadData();
      onDataUpdate?.();
    } catch (error) {
      toast.error('Failed to save property: ' + (error.message || 'Unknown error'));
    }
  };

  const handleDeleteProperty = async (property) => {
    if (property.rooms && property.rooms.length > 0) {
      toast.error(`Cannot delete property "${property.name}". It has ${property.rooms.length} room(s). Please remove all rooms first.`);
      return;
    }

    if (!window.confirm(`Are you sure you want to delete the property "${property.name}"?`)) {
      return;
    }

    try {
      await propertiesService.delete(property.id);
      toast.success('Property deleted successfully');
      loadData();
      onDataUpdate?.();
    } catch (error) {
      toast.error('Failed to delete property: ' + (error.message || 'Unknown error'));
    }
  };

  // Room Type Management Functions
  const openRoomTypeModal = (roomType = null) => {
    setEditingRoomType(roomType);
    setRoomTypeForm({
      name: roomType?.name || '',
      description: roomType?.description || '',
      isShared: roomType?.isShared || false,
      defaultRate: roomType?.defaultRate?.toString() || ''
    });
    setShowRoomTypeModal(true);
  };

  const handleRoomTypeSubmit = async (e) => {
    e.preventDefault();
    if (!roomTypeForm.name.trim() || !roomTypeForm.defaultRate) {
      toast.error('Please fill in all required fields');
      return;
    }

    const rate = parseFloat(roomTypeForm.defaultRate);
    if (isNaN(rate) || rate <= 0) {
      toast.error('Please enter a valid rate');
      return;
    }

    try {
      const roomTypeData = {
        ...roomTypeForm,
        defaultRate: rate
      };

      if (editingRoomType) {
        await roomsService.updateRoomType(editingRoomType.id, roomTypeData);
        toast.success('Room type updated successfully');
      } else {
        await roomsService.createRoomType(roomTypeData);
        toast.success('Room type created successfully');
      }
      
      setShowRoomTypeModal(false);
      setEditingRoomType(null);
      setRoomTypeForm({ name: '', description: '', isShared: false, defaultRate: '' });
      loadData();
      onDataUpdate?.();
    } catch (error) {
      toast.error('Failed to save room type: ' + (error.message || 'Unknown error'));
    }
  };

  const handleDeleteRoomType = async (roomType) => {
    if (roomType.rooms && roomType.rooms.length > 0) {
      toast.error(`Cannot delete room type "${roomType.name}". It is being used by ${roomType.rooms.length} room(s).`);
      return;
    }

    if (!window.confirm(`Are you sure you want to delete the room type "${roomType.name}"?`)) {
      return;
    }

    try {
      await roomsService.deleteRoomType(roomType.id);
      toast.success('Room type deleted successfully');
      loadData();
      onDataUpdate?.();
    } catch (error) {
      toast.error('Failed to delete room type: ' + (error.message || 'Unknown error'));
    }
  };

  if (!isAdmin) {
    return (
      <div style={{ textAlign: 'center', padding: '40px' }}>
        <div style={{ fontSize: '48px', marginBottom: '20px' }}><LockIcon size="1em" aria-hidden="true" /></div>
        <h3>Access Denied</h3>
        <p>You need admin privileges to manage properties and room types.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '40px' }}>
        <div style={{ fontSize: '48px', marginBottom: '20px' }}><RefreshCwIcon size="1em" aria-hidden="true" /></div>
        <div>Loading data...</div>
      </div>
    );
  }

  return (
    <div style={{ padding: isMobile ? '12px' : '20px' }}>
      {/* Header */}
      <div style={{ marginBottom: '30px' }}>
        <h2 style={{ margin: 0, fontSize: isMobile ? '22px' : '28px', fontWeight: '800' }}>Property & Room Type Management</h2>
        <p style={{ margin: '5px 0 0 0', color: '#5F6B61' }}>
          Configure properties and room types before creating rooms
        </p>
      </div>

      {/* Tab Navigation */}
      <div style={{ 
        display: 'flex', 
        gap: '2px', 
        marginBottom: '30px',
        backgroundColor: '#EEEBE1',
        padding: '4px',
        borderRadius: '8px',
        width: isMobile ? '100%' : 'fit-content',
        overflowX: 'auto'
      }}>
        <button
          onClick={() => setActiveTab('properties')}
          style={{
            backgroundColor: activeTab === 'properties' ? '#1A3E2A' : 'transparent',
            color: activeTab === 'properties' ? 'white' : '#5F6B61',
            border: 'none',
            padding: isMobile ? '10px 14px' : '12px 24px',
            borderRadius: '6px',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'all 0.2s ease'
          }}
        >
          Properties ({properties.length})
        </button>
        <button
          onClick={() => setActiveTab('roomTypes')}
          style={{
            backgroundColor: activeTab === 'roomTypes' ? '#1A3E2A' : 'transparent',
            color: activeTab === 'roomTypes' ? 'white' : '#5F6B61',
            border: 'none',
            padding: isMobile ? '10px 14px' : '12px 24px',
            borderRadius: '6px',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'all 0.2s ease'
          }}
        >
          Room Types ({roomTypes.length})
        </button>
      </div>

      {/* Properties Tab */}
      {activeTab === 'properties' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: isMobile ? 'stretch' : 'center', flexDirection: isMobile ? 'column' : 'row', gap: '12px', marginBottom: '20px' }}>
            <h3 style={{ margin: 0, fontSize: '20px', fontWeight: '700' }}>Properties</h3>
            <button
              onClick={() => openPropertyModal()}
              style={{
                backgroundColor: '#1A3E2A',
                color: 'white',
                border: 'none',
                padding: '10px 20px',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              <span><PlusIcon size="1em" aria-hidden="true" /></span>
              Add Property
            </button>
          </div>

          {properties.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px', backgroundColor: '#F5F3EB', borderRadius: '12px' }}>
              <div style={{ fontSize: '64px', marginBottom: '20px' }}><Building2Icon size="1em" aria-hidden="true" /></div>
              <h3 style={{ margin: '0 0 10px 0', color: '#36433B' }}>No properties found</h3>
              <p style={{ color: '#5F6B61', marginBottom: '20px' }}>Properties are required to create rooms</p>
              <button
                onClick={() => openPropertyModal()}
                style={{
                  backgroundColor: '#1A3E2A',
                  color: 'white',
                  border: 'none',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Create First Property
              </button>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(350px, 1fr))', gap: '20px' }}>
              {properties.map(property => (
                <div
                  key={property.id}
                  style={{
                    backgroundColor: 'white',
                    border: '1px solid #E3DFD3',
                    borderRadius: '12px',
                    padding: '20px',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexDirection: isMobile ? 'column' : 'row', gap: '10px', marginBottom: '15px' }}>
                    <div>
                      <h4 style={{ margin: '0 0 8px 0', fontSize: '18px', fontWeight: '700' }}>
                        {property.name}
                      </h4>
                      <p style={{ margin: '0', color: '#5F6B61', fontSize: '14px' }}>
                        {property.address}
                      </p>
                    </div>
                    <div style={{ display: 'flex', gap: '8px', alignSelf: isMobile ? 'flex-end' : 'auto' }}>
                      <button
                        onClick={() => openPropertyModal(property)}
                        style={{
                          backgroundColor: '#B68235',
                          color: 'white',
                          border: 'none',
                          padding: '6px 12px',
                          borderRadius: '6px',
                          fontSize: '12px',
                          cursor: 'pointer'
                        }}
                      >
                        <PencilIcon size="1em" aria-hidden="true" />
                      </button>
                      <button
                        onClick={() => handleDeleteProperty(property)}
                        style={{
                          backgroundColor: '#A13F2F',
                          color: 'white',
                          border: 'none',
                          padding: '6px 12px',
                          borderRadius: '6px',
                          fontSize: '12px',
                          cursor: 'pointer'
                        }}
                      >
                        <Trash2Icon size="1em" aria-hidden="true" />
                      </button>
                    </div>
                  </div>
                  
                  <div style={{ fontSize: '14px', color: '#5F6B61' }}>
                    <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                      <span>{property.rooms?.length || 0} rooms</span>
                      <span>Created {new Date(property.createdAt).toLocaleDateString()}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Room Types Tab */}
      {activeTab === 'roomTypes' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: isMobile ? 'stretch' : 'center', flexDirection: isMobile ? 'column' : 'row', gap: '12px', marginBottom: '20px' }}>
            <h3 style={{ margin: 0, fontSize: '20px', fontWeight: '700' }}>Room Types</h3>
            <button
              onClick={() => openRoomTypeModal()}
              style={{
                backgroundColor: '#1A3E2A',
                color: 'white',
                border: 'none',
                padding: '10px 20px',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              <span><PlusIcon size="1em" aria-hidden="true" /></span>
              Add Room Type
            </button>
          </div>

          {roomTypes.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px', backgroundColor: '#F5F3EB', borderRadius: '12px' }}>
              <div style={{ fontSize: '64px', marginBottom: '20px' }}><HomeIcon size="1em" aria-hidden="true" /></div>
              <h3 style={{ margin: '0 0 10px 0', color: '#36433B' }}>No room types found</h3>
              <p style={{ color: '#5F6B61', marginBottom: '20px' }}>Room types define pricing and configuration for rooms</p>
              <button
                onClick={() => openRoomTypeModal()}
                style={{
                  backgroundColor: '#1A3E2A',
                  color: 'white',
                  border: 'none',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Create First Room Type
              </button>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(350px, 1fr))', gap: '20px' }}>
              {roomTypes.map(roomType => (
                <div
                  key={roomType.id}
                  style={{
                    backgroundColor: 'white',
                    border: '1px solid #E3DFD3',
                    borderRadius: '12px',
                    padding: '20px',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexDirection: isMobile ? 'column' : 'row', gap: '10px', marginBottom: '15px' }}>
                    <div>
                      <h4 style={{ margin: '0 0 8px 0', fontSize: '18px', fontWeight: '700' }}>
                        {roomType.name}
                      </h4>
                      <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '8px', flexWrap: 'wrap' }}>
                        <span style={{
                          backgroundColor: roomType.isShared ? '#B68235' : '#A9613C',
                          color: 'white',
                          padding: '4px 8px',
                          borderRadius: '4px',
                          fontSize: '12px',
                          fontWeight: '600'
                        }}>
                          {roomType.isShared ? 'Shared' : 'Private'}
                        </span>
                        <span style={{ fontSize: '16px', fontWeight: '700', color: '#1A3E2A' }}>
                          ₹{roomType.defaultRate}/night
                        </span>
                      </div>
                      {roomType.description && (
                        <p style={{ margin: '0', color: '#5F6B61', fontSize: '14px' }}>
                          {roomType.description}
                        </p>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: '8px', flexDirection: 'column', width: isMobile ? '100%' : 'auto' }}>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button
                          onClick={() => openRoomTypeModal(roomType)}
                          style={{
                            backgroundColor: '#B68235',
                            color: 'white',
                            border: 'none',
                            padding: '6px 12px',
                            borderRadius: '6px',
                            fontSize: '12px',
                            cursor: 'pointer'
                          }}
                        >
                          <PencilIcon size="1em" aria-hidden="true" />
                        </button>
                        <button
                          onClick={() => handleDeleteRoomType(roomType)}
                          style={{
                            backgroundColor: '#A13F2F',
                            color: 'white',
                            border: 'none',
                            padding: '6px 12px',
                            borderRadius: '6px',
                            fontSize: '12px',
                            cursor: 'pointer'
                          }}
                        >
                          <Trash2Icon size="1em" aria-hidden="true" />
                        </button>
                      </div>
                      <PriceBreakdownButton
                        entityType="roomType"
                        entityId={roomType.id}
                        entity={{ name: roomType.name, basePrice: roomType.defaultRate }}
                        onUpdate={loadData}
                        variant="primary"
                        size="small"
                      />
                    </div>
                  </div>
                  
                  <div style={{ fontSize: '14px', color: '#5F6B61' }}>
                    <span>{roomType.rooms?.length || 0} rooms using this type</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Property Modal */}
      {showPropertyModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: '#E9DFCE',
            padding: '30px',
            borderRadius: '12px',
            width: '500px',
            maxWidth: '90vw',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            <h3 style={{ margin: '0 0 20px 0', fontSize: '24px', fontWeight: '700' }}>
              {editingProperty ? 'Edit Property' : 'Add New Property'}
            </h3>
            
            <form onSubmit={handlePropertySubmit}>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                  Property Name *
                </label>
                <input
                  type="text"
                  value={propertyForm.name}
                  onChange={(e) => setPropertyForm({ ...propertyForm, name: e.target.value })}
                  placeholder="e.g., Main Campus, Retreat Center"
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #D2CDBF',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                  required
                />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                  Address *
                </label>
                <textarea
                  value={propertyForm.address}
                  onChange={(e) => setPropertyForm({ ...propertyForm, address: e.target.value })}
                  placeholder="Enter full address"
                  rows={3}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #D2CDBF',
                    borderRadius: '8px',
                    fontSize: '14px',
                    resize: 'vertical'
                  }}
                  required
                />
              </div>

              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', flexDirection: isMobile ? 'column' : 'row' }}>
                <button
                  type="button"
                  onClick={() => {
                    setShowPropertyModal(false);
                    setEditingProperty(null);
                    setPropertyForm({ name: '', address: '' });
                  }}
                  style={{
                    backgroundColor: '#5F6B61',
                    color: 'white',
                    border: 'none',
                    padding: '12px 24px',
                    borderRadius: '8px',
                    fontSize: '14px',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={{
                    backgroundColor: editingProperty ? '#B68235' : '#1A3E2A',
                    color: 'white',
                    border: 'none',
                    padding: '12px 24px',
                    borderRadius: '8px',
                    fontSize: '14px',
                    cursor: 'pointer'
                  }}
                >
                  {editingProperty ? 'Update Property' : 'Create Property'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Room Type Modal */}
      {showRoomTypeModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: '#E9DFCE',
            padding: '30px',
            borderRadius: '12px',
            width: '500px',
            maxWidth: '90vw',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            <h3 style={{ margin: '0 0 20px 0', fontSize: '24px', fontWeight: '700' }}>
              {editingRoomType ? 'Edit Room Type' : 'Add New Room Type'}
            </h3>
            
            <form onSubmit={handleRoomTypeSubmit}>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                  Room Type Name *
                </label>
                <input
                  type="text"
                  value={roomTypeForm.name}
                  onChange={(e) => setRoomTypeForm({ ...roomTypeForm, name: e.target.value })}
                  placeholder="e.g., Standard Room, Deluxe Suite"
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #D2CDBF',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                  required
                />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                  Description
                </label>
                <textarea
                  value={roomTypeForm.description}
                  onChange={(e) => setRoomTypeForm({ ...roomTypeForm, description: e.target.value })}
                  placeholder="Brief description of the room type"
                  rows={3}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #D2CDBF',
                    borderRadius: '8px',
                    fontSize: '14px',
                    resize: 'vertical'
                  }}
                />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                  Default Rate (per night) *
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={roomTypeForm.defaultRate}
                  onChange={(e) => setRoomTypeForm({ ...roomTypeForm, defaultRate: e.target.value })}
                  placeholder="0.00"
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #D2CDBF',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                  required
                />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <input
                    type="checkbox"
                    checked={roomTypeForm.isShared}
                    onChange={(e) => setRoomTypeForm({ ...roomTypeForm, isShared: e.target.checked })}
                  />
                  <span style={{ fontWeight: '600' }}>Shared Room Type</span>
                </label>
                <small style={{ color: '#5F6B61', marginLeft: '28px' }}>
                  Shared rooms can have multiple beds for different guests
                </small>
              </div>

              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', flexDirection: isMobile ? 'column' : 'row' }}>
                <button
                  type="button"
                  onClick={() => {
                    setShowRoomTypeModal(false);
                    setEditingRoomType(null);
                    setRoomTypeForm({ name: '', description: '', isShared: false, defaultRate: '' });
                  }}
                  style={{
                    backgroundColor: '#5F6B61',
                    color: 'white',
                    border: 'none',
                    padding: '12px 24px',
                    borderRadius: '8px',
                    fontSize: '14px',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={{
                    backgroundColor: editingRoomType ? '#B68235' : '#1A3E2A',
                    color: 'white',
                    border: 'none',
                    padding: '12px 24px',
                    borderRadius: '8px',
                    fontSize: '14px',
                    cursor: 'pointer'
                  }}
                >
                  {editingRoomType ? 'Update Room Type' : 'Create Room Type'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default PropertyRoomTypeManager;